import type {
	PortalClientDto,
	PortalProfileDto,
	PortalSiteDisplaySettingsDto,
} from '@supply-mover-2-0/shared';
import { create } from 'zustand';

/**
 * Key used to persist portal auth state to localStorage for:
 *   1. Session restore on page reload
 *   2. Cross-tab synchronization (TokenManager listens for storage events)
 *
 * Single unscoped key per origin. Different portal brands live on
 * different subdomains, which the browser treats as distinct origins —
 * each gets its own isolated localStorage bucket. A previous attempt to
 * scope by `portalSiteId` within the key raced with the portal-site
 * bootstrap on reload (store empty at restore time → session appeared
 * lost even though the token was still on disk).
 */
export const PORTAL_AUTH_STORAGE_KEY = 'workd:portal_auth:state';

interface PortalAuthState {
	token: string | null;
	refreshToken: string | null;
	/** Timestamp (ms) when the access token expires */
	expiresAt: number | null;
	profile: PortalProfileDto | null;
	isAuthenticated: boolean;
	isLoading: boolean;
	/**
	 * True while the initial-mount session-restore is still in flight.
	 *
	 * Seeded from a synchronous localStorage peek at module load so the
	 * very first render of route guards (PortalLayout) already knows a
	 * restore is pending — otherwise the guards see `isAuthenticated=false`
	 * before usePortalAuth's useEffect has had a chance to fire and
	 * redirect away from the user's deep link (e.g. /orders → /catalog
	 * on every refresh).
	 *
	 * Cleared once /auth/me resolves or fails, or immediately if there
	 * was no persisted token to restore.
	 */
	isRestoringSession: boolean;
	/** All clients assigned to this portal user */
	availableClients: Array<PortalClientDto>;
	/** Currently selected client ID (from Redis session) */
	activeClientId: number | null;
	/** True when user has multiple clients and hasn't selected one yet */
	needsClientSelection: boolean;
	/** Unique tab identifier for cross-tab lock coordination */
	tabId: string | null;
	/** User's language preference from _user._languagePreference */
	languagePreference: string | null;
	/**
	 * Effective storefront display flags for the authenticated visitor.
	 * Sourced from /auth/me; null while logged out (the public bootstrap
	 * carries the anonymous-visitor flags via portalSiteStore).
	 */
	displaySettings: PortalSiteDisplaySettingsDto | null;

	setToken: (token: string | null) => void;
	setRefreshToken: (refreshToken: string | null) => void;
	setExpiresAt: (expiresAt: number | null) => void;
	setProfile: (profile: PortalProfileDto | null) => void;
	setAuthenticated: (value: boolean) => void;
	setLoading: (value: boolean) => void;
	setRestoringSession: (value: boolean) => void;
	setAvailableClients: (clients: Array<PortalClientDto>) => void;
	setActiveClientId: (clientId: number | null) => void;
	setNeedsClientSelection: (value: boolean) => void;
	setTabId: (tabId: string) => void;
	setLanguagePreference: (languagePreference: string | null) => void;
	setDisplaySettings: (settings: PortalSiteDisplaySettingsDto | null) => void;
	/** Persist token-related fields to localStorage for cross-tab sync */
	persist: () => void;
	reset: () => void;
}

const initialState = {
	token: null,
	refreshToken: null,
	expiresAt: null,
	profile: null,
	isAuthenticated: false,
	isLoading: false,
	isRestoringSession: false,
	availableClients: [],
	activeClientId: null,
	needsClientSelection: false,
	tabId: null,
	languagePreference: null,
	displaySettings: null,
};

/**
 * Synchronous localStorage peek that runs at module load.
 *
 * Route guards (PortalLayout) decide whether to redirect on the very
 * first render — before useEffect has a chance to fire and kick off
 * the async /auth/me restore. If we don't seed the store with
 * "we're about to restore" state at creation time, the first render
 * sees `isAuthenticated=false` and bounces the user away from any
 * deep link (refreshing on /orders ends up on /catalog). Peeking the
 * persisted key here lets us start the very first render with
 * `isRestoringSession=true` so guards know to wait.
 */
function hasPersistedSessionAtLoad(): boolean {
	try {
		return localStorage.getItem(PORTAL_AUTH_STORAGE_KEY) != null;
	} catch {
		return false;
	}
}

// `parsePersisted` only returns a slice when both tokens are present, so the
// type carries that guarantee (NonNullable) rather than the store's nullable
// at-rest field types.
type PersistedSlice = Pick<
	PortalAuthState,
	'expiresAt' | 'languagePreference'
> & {
	token: NonNullable<PortalAuthState['token']>;
	refreshToken: NonNullable<PortalAuthState['refreshToken']>;
};

function parsePersisted(raw: string | null): PersistedSlice | null {
	if (raw == null) return null;
	try {
		const parsed = JSON.parse(raw);
		if (parsed?.token != null && parsed?.refreshToken != null) {
			return {
				token: parsed.token,
				refreshToken: parsed.refreshToken,
				expiresAt: parsed.expiresAt ?? null,
				languagePreference: parsed.languagePreference ?? null,
			};
		}
	} catch {
		/* malformed json — treat as missing */
	}
	return null;
}

/**
 * Read persisted auth state from localStorage.
 *
 * Returns whatever we find at the canonical unscoped key. As a one-time
 * migration, also accepts keys of the shape `workd:portal_auth:state:<N>`
 * — a brief earlier attempt at brand-scoped storage wrote there before
 * the key scheme was reverted. The first valid match is adopted into the
 * unscoped key and the scoped variant is cleaned up, so users with live
 * sessions from that window don't appear logged out after reload.
 */
function loadPersistedState(): PersistedSlice | null {
	const canonical = parsePersisted(
		localStorage.getItem(PORTAL_AUTH_STORAGE_KEY),
	);
	if (canonical != null) {
		return canonical;
	}

	for (let i = 0; i < localStorage.length; i++) {
		const key = localStorage.key(i);
		if (key == null || !key.startsWith(`${PORTAL_AUTH_STORAGE_KEY}:`)) {
			continue;
		}

		const legacyScoped = parsePersisted(localStorage.getItem(key));
		if (legacyScoped == null) {
			continue;
		}

		// Migrate into the canonical key and drop the scoped variant.
		localStorage.setItem(PORTAL_AUTH_STORAGE_KEY, JSON.stringify(legacyScoped));
		localStorage.removeItem(key);
		return legacyScoped;
	}

	return null;
}

// Peek the persisted slice once at module load so the store's
// initial state (the snapshot the very first render sees) already
// reflects whatever was in localStorage. Without this, both the
// route guard and TranslationProvider's `getPreferredLanguage()`
// run against `languagePreference: null` on the first render and
// only flip after the async /auth/me restore lands — manifesting
// as "language reverts to English on every reload until the
// authenticated session bounces back". Same `loadPersistedState()`
// helper that `usePortalAuth` calls during the restore — we just
// run it earlier.
const _persistedAtLoad = loadPersistedState();

export const usePortalAuthStore = create<PortalAuthState>((set, get) => ({
	...initialState,
	// Override the default `false` from initialState — if there's a
	// persisted token in localStorage, mark restore as pending before the
	// first render so route guards wait instead of redirecting.
	isRestoringSession: hasPersistedSessionAtLoad(),
	// Synchronously seed `languagePreference` from the persisted slice
	// so TranslationProvider's first paint resolves to the user's
	// chosen language. The token / refreshToken / expiresAt fields
	// remain null in the initial render — they're set once
	// `usePortalAuth`'s effect confirms the session via /auth/me — but
	// language preference is a stable user choice that's safe to honor
	// before the server round-trip completes (worst case a stale
	// preference for a few hundred ms; far better than the English
	// flicker on every reload).
	languagePreference: _persistedAtLoad?.languagePreference ?? null,
	setToken: (token) => set({ token }),
	setRefreshToken: (refreshToken) => set({ refreshToken }),
	setExpiresAt: (expiresAt) => set({ expiresAt }),
	setProfile: (profile) => set({ profile }),
	setAuthenticated: (value) => set({ isAuthenticated: value }),
	setLoading: (value) => set({ isLoading: value }),
	setRestoringSession: (value) => set({ isRestoringSession: value }),
	setAvailableClients: (clients) => set({ availableClients: clients }),
	setActiveClientId: (clientId) => set({ activeClientId: clientId }),
	setNeedsClientSelection: (value) => set({ needsClientSelection: value }),
	setTabId: (tabId) => set({ tabId }),
	setLanguagePreference: (languagePreference) => set({ languagePreference }),
	setDisplaySettings: (settings) => set({ displaySettings: settings }),

	persist: () => {
		const { token, refreshToken, expiresAt, languagePreference } = get();
		if (token != null && refreshToken != null) {
			localStorage.setItem(
				PORTAL_AUTH_STORAGE_KEY,
				JSON.stringify({ token, refreshToken, expiresAt, languagePreference }),
			);
		}
	},

	reset: () => {
		localStorage.removeItem(PORTAL_AUTH_STORAGE_KEY);
		set(initialState);
	},
}));

/**
 * Attempt to restore a previous session from localStorage.
 * Called once at app startup. Returns the stored tokens if they exist
 * so the caller can attempt a session refresh.
 */
export function restorePersistedSession(): PersistedSlice | null {
	return loadPersistedState();
}
